<template>
  <div class="filter-sidebar-container w-full border border-iron bg-white rounded-md p-4 shadow-sm">
    <div class="flex justify-between items-center mb-4 pb-2 border-b border-iron">
      <span class="font-bold text-secondary text-base uppercase tracking-wider">Filtros</span>
      <button 
        v-if="hasActiveFilters" 
        @click="clearAllFilters" 
        class="text-xs text-blog-red hover:underline font-mono"
      >
        {{ translate('clear all', 'ucfirst') }}
      </button>
    </div>
    
    <LoaderComponent v-if="loadingFilters" />
    <template v-else>

      <div class="filter-group border-b border-iron last:border-none py-3">
        <button 
          @click="toggleSection('language')"
          class="flex justify-between items-center w-full font-semibold text-secondary text-sm focus:outline-none"
        >
          <span>{{ translate('language', 'ucfirst') }}</span>
          <i :class="['pi text-xs transition-transform duration-200', sections.language ? 'pi-chevron-up' : 'pi-chevron-down']"></i>
        </button>

        <div v-show="sections.language" class="filter-content mt-3 space-y-2.5 transition-all">
          <div 
            v-for="lang in options.languages" 
            :key="lang.id" 
            class="flex items-center justify-between w-full text-sm group cursor-pointer"
          >
            <div class="flex items-center gap-2.5">
              <input 
                type="checkbox" 
                :id="'lang-' + lang.id"
                :value="lang.code"
                v-model="selectedFilters.languages"
                @change="emitUpdate"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
              <label :for="'lang-' + lang.id" class="text-gray-700 group-hover:text-secondary cursor-pointer select-none">
                {{ lang.getName(true, true) }}
              </label>
            </div>
            <span class="text-xs text-gray-400 font-mono">({{ lang.total }})</span>
          </div>
        </div>
      </div>

      <div class="filter-group border-b border-iron last:border-none py-3">
        <button 
          @click="toggleSection('category')"
          class="flex justify-between items-center w-full font-semibold text-secondary text-sm focus:outline-none"
        >
          <span>{{ translate('course level', 'ucfirst') }}</span>
          <i :class="['pi text-xs transition-transform duration-200', sections.category ? 'pi-chevron-up' : 'pi-chevron-down']"></i>
        </button>
        
        <div v-show="sections.category" class="filter-content mt-3 space-y-2.5 transition-all">
          <div 
            v-for="category in options.categories" 
            :key="category.id" 
            class="flex items-center justify-between w-full text-sm group cursor-pointer"
          >
            <div class="flex items-center gap-2.5">
              <input 
                type="checkbox" 
                :id="'category-' + category.id"
                :value="category.id"
                v-model="selectedFilters.categories"
                @change="emitUpdate"
                class="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer"
              />
              <label :for="'category-' + category.id" class="text-gray-700 group-hover:text-secondary cursor-pointer select-none">
                {{ capitalizeWords(category.getTranslatedName()) }}
              </label>
            </div>
            <span class="text-xs text-gray-400 font-mono">({{ category.total }})</span>
          </div>
        </div>
      </div>
    </template>

  </div>
</template>

<script setup>
import CourseService from '@/services/CourseService';
import { reactive, computed, defineEmits, onMounted, ref } from 'vue';
import LoaderComponent from '../Utils/LoaderComponent.vue';
import { capitalizeWords } from '@/composables/textUtils';
import { translate } from '@/composables/translate.js';

const loadingFilters = ref(true);
const emit = defineEmits(['filter-change']);

const options = reactive({
  languages: [],
  categories: []
});

const sections = reactive({
  language: true,
  category: true
});

const selectedFilters = reactive({
  languages: [],
  categories: []
});

const hasActiveFilters = computed(() => {
  return selectedFilters.languages.length > 0 || selectedFilters.categories.length > 0;
});

onMounted(() => {
  getFiltersData();
})

/**
 * Loads languages & categories filter data
 */
async function getFiltersData() {
  loadingFilters.value = true;
  options.languages = false;
  options.categories = false;

  const response = await CourseService.getFiltersData();
  
  options.languages = response.languages;
  options.categories = response.categories

  loadingFilters.value = false;
}

function toggleSection(section) {
  sections[section] = !sections[section];
}

function emitUpdate() {
  emit('filter-change', {
    languages: selectedFilters.languages,
    categories: selectedFilters.categories
  });
}

function clearAllFilters() {
  selectedFilters.languages = [];
  selectedFilters.categories = [];
  emitUpdate();
}
</script>